import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import {
  LumpsumAllocationCategoryExecutionStatus,
  type LumpsumAllocationSellRecommendation
} from '../../../../api/allocation.types'
import { AllocationMovementToggle, PlanCategoryBucketKey } from '../../../../api/types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { usePlanSellAction } from '../../../../../execution/sell/usePlanSellAction'
import { useLumpsumPlansContext } from '../../../LumpsumPlansProvider'
import { AllocationFundCardTrailingLayout } from '../../../lumpsum-ui-components/AllocationFundCardTrailingLayout'
import {
  PlansFundCard,
  type PlanFundCardCta,
  type PlanFundCardInsight
} from '../../../lumpsum-ui-components/PlansFundCard'
import { buildAllocationInsightsFromTags } from '../allocationInsightTags'
import { planPercentageLabel } from '../../../../../display-values/lumpsumPlanPercentage'
import { formatFundIdealRangeTrailingLabel } from '../../../../../utils/lumpsum/planListUtils'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type AllocationSellFundCardProps = {
  recommendation: LumpsumAllocationSellRecommendation
  categoryName: string
  showBottomDivider?: boolean
}

export function AllocationSellFundCard({
  recommendation,
  categoryName,
  showBottomDivider = true
}: AllocationSellFundCardProps) {
  const { openPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const { allocationPlansData } = useLumpsumPlansContext()
  const { onSell } = usePlanSellAction({ transactionCta: allocationPlansData?.transactionCta })
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.LumpsumAllocation
  const fundRole = PlansFundRole.Sell
  const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.sellAmount ?? 0 })

  const openPlanBottomSheet = () => {
    openPlanRecommendationBottomSheet({
      isin,
      bucketType: PlanCategoryBucketKey.sellRecommendations
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-action`,
      label: 'Sell',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Sell', fundName, fundIsin: isin })
        onSell({
          isin,
          fundName,
          amount: sellRecommendationFormattedAmount.value,
          currentValue: recommendation?.currentValue ?? 0,
          isFullWithdrawal: isAllocationFullSell(recommendation)
        })
      },
      variant: 'tertiary'
    },
    {
      id: `${isin}-view-plan`,
      label: 'View plan',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'View plan', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<AllocationSellFundCardTrailing recommendation={recommendation} />}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      insights={buildAllocationSellInsights(recommendation, categoryName)}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

export function buildAllocationSellInsights(
  recommendation: LumpsumAllocationSellRecommendation,
  categoryName: string
): PlanFundCardInsight[] {
  return buildAllocationInsightsFromTags({
    tags: recommendation?.tags,
    isin: recommendation?.isin ?? '',
    categoryName,
    missedGains: recommendation?.missedGains
  })
}

export function AllocationSellFundCardTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationSellRecommendation
}) {
  const { allocationMovementToggle } = useLumpsumPlansContext()

  if (allocationMovementToggle === AllocationMovementToggle.FUND_MOVEMENT) {
    return <AllocationSellFundMovementTrailing recommendation={recommendation} />
  }

  return <AllocationSellAllocationMovementTrailing recommendation={recommendation} />
}

export function AllocationSellFundMovementTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationSellRecommendation
}) {
  const sellAmount = recommendation?.sellAmount ?? 0
  const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: sellAmount })
  const isFullSell = isAllocationFullSell(recommendation)

  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={`Sell ${sellRecommendationFormattedAmount.label}`}
      secondaryIconHtml={
        isFullSell
          ? ALLOCATION_SELL_FUND_CARD_FULL_SELL_ICON_SVG
          : ALLOCATION_SELL_FUND_CARD_PARTIAL_SELL_ICON_SVG
      }
      secondaryIconClassName={isFullSell ? 'rotate-180' : undefined}
      secondaryText={isFullSell ? 'Full Sell' : 'Partial Sell'}
    />
  )
}

export function AllocationSellAllocationMovementTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationSellRecommendation
}) {
  const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.sellAmount ?? 0 })
  const sellAmountLabel = `Sell ${sellRecommendationFormattedAmount.label}`

  // Normal sell direction only — current% → ideal absolute%.
  if (recommendation?.executionStatus === LumpsumAllocationCategoryExecutionStatus.ACTIONABLE_DECREASE) {
    return (
      <AllocationFundCardTrailingLayout
        primaryLabel={sellAmountLabel}
        secondaryIconHtml={ALLOCATION_SELL_FUND_CARD_PIE_ICON_SVG}
        secondaryText={`${planPercentageLabel({ rawPercentage: recommendation?.currentAllocationPercentage ?? 0 })} → ${planPercentageLabel({ rawPercentage: recommendation?.idealAllocationPercentage?.absolute ?? 0 })}`}
      />
    )
  }

  // Reverse + in-progress / completed / already-ideal — lower–upper% ideal.
  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={sellAmountLabel}
      secondaryText={formatFundIdealRangeTrailingLabel(recommendation?.idealAllocationPercentage)}
    />
  )
}

export function isAllocationFullSell(recommendation: LumpsumAllocationSellRecommendation): boolean {
  const sellAmount = recommendation?.sellAmount ?? 0
  const currentValue = recommendation?.currentValue ?? 0
  const idealAbsolute = recommendation?.idealAllocationPercentage?.absolute
  return (
    (typeof idealAbsolute === 'number' && idealAbsolute === 0) ||
    (currentValue > 0 && Math.abs(sellAmount - currentValue) <= currentValue * 0.01)
  )
}

export const ALLOCATION_SELL_FUND_CARD_PIE_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.2831 1.92969C4.33555 1.92969 3.40927 2.21067 2.62141 2.7371C1.83355 3.26353 1.21949 4.01177 0.856876 4.88719C0.494263 5.76262 0.399387 6.72591 0.584246 7.65526C0.769104 8.5846 1.22539 9.43826 1.89541 10.1083C2.56544 10.7783 3.41909 11.2346 4.34844 11.4194C5.27778 11.6043 6.24108 11.5094 7.1165 11.1468C7.99192 10.7842 8.74016 10.1701 9.26659 9.38228C9.79303 8.59442 10.074 7.66815 10.074 6.7206H5.52264C5.45911 6.7206 5.39818 6.69536 5.35326 6.65044C5.30834 6.60551 5.2831 6.54458 5.2831 6.48105V1.92969Z" fill="#3D3D3D"/><path d="M6.7168 5.03955C6.7168 5.10318 6.74203 5.16411 6.78696 5.20903C6.83188 5.25395 6.89281 5.27919 6.95634 5.27919H11.5077C11.5077 4.00856 11.003 2.78998 10.1045 1.89151C9.20601 0.993036 7.98743 0.488281 6.7168 0.488281V5.03955Z" fill="#3D3D3D"/></svg>`

export const ALLOCATION_SELL_FUND_CARD_FULL_SELL_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12.25 12.25" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.125 1.125C3.36358 1.125 1.125 3.36357 1.125 6.125C1.125 8.8864 3.36358 11.125 6.125 11.125C8.3638 11.125 10.259 9.65355 10.8961 7.625H11.936C11.2699 10.2128 8.92075 12.125 6.125 12.125C2.81129 12.125 0.125 9.4387 0.125 6.125C0.125 2.81129 2.81129 0.125 6.125 0.125C8.92075 0.125 11.2699 2.03716 11.936 4.625H10.8961C10.259 2.59644 8.3638 1.125 6.125 1.125ZM3.79282 5.7513L6.0428 3.75129L6.7072 4.4987L5.4401 5.625H12.125V6.625H5.4401L6.7072 7.7513L6.0428 8.4987L3.79282 6.4987C3.68608 6.4038 3.625 6.2678 3.625 6.125C3.625 5.9822 3.68608 5.8462 3.79282 5.7513Z" fill="#3D3D3D" stroke="#3D3D3D" stroke-width="0.25"/></svg>`

export const ALLOCATION_SELL_FUND_CARD_PARTIAL_SELL_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12.25 12.25" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.45718 6.62206L6.2072 8.62207L5.5428 7.87466L6.8099 6.74836L0.125 6.74836L0.125 5.74836L6.8099 5.74837L5.5428 4.62206L6.2072 3.87467L8.45718 5.87467C8.56393 5.96957 8.625 6.10557 8.625 6.24837C8.625 6.39117 8.56393 6.52716 8.45718 6.62206Z" fill="#3D3D3D" stroke="#3D3D3D" stroke-width="0.25"/><path d="M11.125 6.125C11.125 8.88642 8.88643 11.125 6.125 11.125L6.125 12.125C9.43871 12.125 12.125 9.43871 12.125 6.125C12.125 2.8113 9.43871 0.125 6.125 0.125L6.125 1.125C8.88643 1.125 11.125 3.3636 11.125 6.125Z" fill="#3D3D3D" stroke="#3D3D3D" stroke-width="0.25"/></svg>`
